import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Plus, FileText, CircleGauge, Factory,
  Users, MessageSquare, Bell, User, LogOut, Menu, X, Inbox, Send
} from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { NavBadge } from "./Primitives";
import { countUnreadNotifications, subscribeNotifications, getNavCounters } from "../lib/db";

const NAV = [
  { to: "/app/dashboard", icon: LayoutDashboard, key: "dashboard", testid: "nav-dashboard", counterKey: null },
  { to: "/app/requests", icon: FileText, key: "requests", testid: "nav-requests", counterKey: "newOpportunities" },
  { to: "/app/new-request", icon: Plus, key: "newRequest", testid: "nav-new-request", primary: true },
  { to: "/app/producers", icon: Factory, key: "producers", testid: "nav-producers", counterKey: "newProfiles" },
  { to: "/app/messages", icon: MessageSquare, key: "messages", testid: "nav-messages", counterKey: "newMessages" },
];

const SIDE_EXTRA = [
  { to: "/app/my-requests", icon: Inbox, key: "myRequests", testid: "nav-my-requests" },
  { to: "/app/my-proposals", icon: Send, key: "myProposals", testid: "nav-my-proposals" },
  { to: "/app/availability", icon: CircleGauge, key: "availability", testid: "nav-availability" },
  { to: "/app/community", icon: Users, key: "community", testid: "nav-community" },
  { to: "/app/notifications", icon: Bell, key: "notifications", testid: "nav-notifications", counterKey: "notifications" },
  { to: "/app/profile", icon: User, key: "profile", testid: "nav-profile" },
];

export function LangToggle() {
  const { lang, toggle } = useI18n();
  return (
    <button
      onClick={toggle}
      data-testid="lang-toggle"
      className="font-mono text-[11px] tracking-widest uppercase px-2.5 py-1 rounded-md border border-carbon-600 text-ink-700 hover:text-ink-900 hover:border-electric-500 transition-colors"
      aria-label="Toggle language"
    >
      {lang === "it" ? "IT · en" : "EN · it"}
    </button>
  );
}

export function Logo({ size = "md" }) {
  const dim = size === "lg" ? "text-2xl" : "text-lg";
  return (
    <div className="flex items-center gap-2" data-testid="brand-logo">
      <div className="relative w-8 h-8 rounded-md bg-gradient-to-br from-electric-500 to-electric-600 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,.2) 0 2px, transparent 2px 4px)"
        }} />
        <span className="font-mono font-bold text-ink-900 text-sm relative">R4</span>
      </div>
      <span className={`${dim} font-semibold tracking-tight`}>R4Pro</span>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, testid, counter, mobileClose, pulse = false }) {
  const hasCount = counter > 0;
  return (
    <NavLink
      to={to}
      onClick={mobileClose}
      data-testid={testid}
      className={({ isActive }) =>
        `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isActive
            ? "bg-electric-500/10 text-electric-600 border border-electric-500/40"
            : hasCount
              ? "text-ink-900 bg-carbon-800/70 border border-carbon-600 nav-illuminated hover:bg-carbon-750"
              : "text-ink-700 hover:text-ink-900 hover:bg-carbon-800 border border-transparent"
        }`
      }
    >
      <span className={pulse ? "relative" : ""}>
        <Icon className={`w-4 h-4 ${pulse ? "text-electric-500" : ""}`} />
        {pulse && (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-electric-500 pulse-electric" data-testid={`${testid}-glow`} />
        )}
      </span>
      <span className="flex-1">{label}</span>
      <NavBadge count={counter} />
    </NavLink>
  );
}

export default function AppShell({ children }) {
  const { t } = useI18n();
  const { signOut, user, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [unreadNotif, setUnreadNotif] = useState(0);
  const [navCounts, setNavCounts] = useState({ newOpportunities: 0, newProfiles: 0, newMessages: 0 });

  const refreshNavCounts = React.useCallback(() => {
    if (!user?.id) { setNavCounts({ newOpportunities: 0, newProfiles: 0, newMessages: 0 }); return; }
    getNavCounters(user.id).then((c) => setNavCounts(c)).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) { setUnreadNotif(0); return; }
    let alive = true;
    countUnreadNotifications(user.id).then((n) => { if (alive) setUnreadNotif(n); });
    refreshNavCounts();
    const unsub = subscribeNotifications(user.id, () => {
      countUnreadNotifications(user.id).then((n) => { if (alive) setUnreadNotif(n); });
      refreshNavCounts();
    });
    // Refresh on route change / when user returns to tab / every 90s / on custom event
    const onFocus = () => refreshNavCounts();
    const onRefreshEvent = () => refreshNavCounts();
    window.addEventListener("focus", onFocus);
    window.addEventListener("r4pro:refresh-nav", onRefreshEvent);
    const interval = setInterval(refreshNavCounts, 90000);
    return () => {
      alive = false; unsub && unsub();
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("r4pro:refresh-nav", onRefreshEvent);
      clearInterval(interval);
    };
  }, [user?.id, refreshNavCounts]);

  // Also refresh whenever the user navigates within the app
  useEffect(() => { refreshNavCounts(); }, [location.pathname, refreshNavCounts]);

  const getCounter = (n) => {
    if (!n.counterKey) return 0;
    if (n.counterKey === "notifications") return unreadNotif;
    // People & Workshops badge is admin-only. Normal users never see it.
    // The directory itself remains fully functional for everyone.
    if (n.counterKey === "newProfiles" && !isAdmin) return 0;
    return navCounts[n.counterKey] || 0;
  };

  return (
    <div className="min-h-screen bg-carbon-900 text-ink-900 flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-carbon-600 bg-carbon-850 flex-col">
        <div className="px-5 py-5 border-b border-carbon-600">
          <Logo size="lg" />
          <p className="mt-2 text-xs text-ink-600 font-mono tracking-wider uppercase">
            {t("brand.tagline")}
          </p>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {[...NAV.filter(n => !n.primary), ...SIDE_EXTRA].map((n) => {
            const counter = getCounter(n);
            return (
              <NavItem
                key={n.to}
                to={n.to}
                icon={n.icon}
                testid={n.testid}
                label={t(`nav.${n.key}`)}
                counter={counter}
                pulse={n.testid === "nav-notifications" && counter > 0}
              />
            );
          })}
        </nav>
        <div className="p-3 border-t border-carbon-600 space-y-2">
          <button
            onClick={() => navigate("/app/new-request")}
            data-testid="sidebar-new-request-btn"
            className="w-full flex items-center justify-center gap-2 bg-electric-500 hover:bg-electric-400 text-ink-900 text-sm font-medium rounded-lg px-3 py-2.5 transition-colors electric-glow"
          >
            <Plus className="w-4 h-4" />
            {t("nav.newRequest")}
          </button>
          <div className="flex items-center justify-between px-1">
            <LangToggle />
            <button
              onClick={async () => { await signOut(); navigate("/"); }}
              data-testid="sidebar-logout-btn"
              className="text-xs text-ink-600 hover:text-ink-900 flex items-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" /> {t("nav.logout")}
            </button>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-40 bg-carbon-900/95 backdrop-blur-xl border-b border-carbon-600">
          <div className="flex items-center justify-between px-4 h-14">
            <button
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
              className="p-2 -ml-2 text-ink-700 hover:text-ink-900"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Logo />
            <div className="flex items-center gap-2">
              <LangToggle />
              <button
                onClick={() => navigate("/app/notifications")}
                data-testid="header-notifications-btn"
                className="relative p-2 text-ink-700 hover:text-ink-900"
                aria-label="Notifications"
              >
                <Bell className={`w-5 h-5 ${unreadNotif > 0 ? "text-electric-500" : ""}`} />
                {unreadNotif > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-electric-500 rounded-full pulse-electric" data-testid="header-notifications-glow" />
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)}>
            <div
              className="absolute left-0 top-0 bottom-0 w-72 bg-carbon-900 border-r border-carbon-600 p-4 animate-fade-up"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <Logo />
                <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" className="p-2 text-ink-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="space-y-1">
                {[...NAV.filter(n => !n.primary), ...SIDE_EXTRA].map((n) => {
                  const counter = getCounter(n);
                  return (
                    <NavItem
                      key={n.to}
                      to={n.to}
                      icon={n.icon}
                      testid={`drawer-${n.testid}`}
                      label={t(`nav.${n.key}`)}
                      counter={counter}
                      pulse={n.testid === "nav-notifications" && counter > 0}
                      mobileClose={() => setMobileOpen(false)}
                    />
                  );
                })}
              </nav>
              <button
                onClick={() => { setMobileOpen(false); navigate("/"); }}
                data-testid="drawer-logout"
                className="mt-6 w-full flex items-center gap-2 text-ink-600 hover:text-ink-900 text-sm px-3 py-2"
              >
                <LogOut className="w-4 h-4" /> {t("nav.logout")}
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 pb-24 lg:pb-8" data-testid="main-content">
          {children}
        </main>

        {/* Bottom nav (mobile) */}
        <nav
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-carbon-900/95 backdrop-blur-xl border-t border-carbon-600"
          data-testid="bottom-nav"
        >
          <div className="grid grid-cols-5 h-16">
            {NAV.map((n) => {
              const active = location.pathname.startsWith(n.to);
              const Icon = n.icon;
              const count = getCounter(n);
              if (n.primary) {
                return (
                  <NavLink key={n.to} to={n.to} data-testid={`bn-${n.testid}`} className="flex items-center justify-center">
                    <div className="w-12 h-12 -mt-4 rounded-full bg-electric-500 flex items-center justify-center shadow-[0_8px_24px_rgba(46,107,255,0.5)]">
                      <Icon className="w-5 h-5 text-ink-900" />
                    </div>
                  </NavLink>
                );
              }
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`bn-${n.testid}`}
                  className={`relative flex flex-col items-center justify-center gap-1 text-[10px] ${
                    active ? "text-electric-600" : "text-ink-600"
                  }`}
                >
                  <div className="relative">
                    <Icon className="w-5 h-5" />
                    {count > 0 && (
                      <span className="absolute -top-1 -right-2">
                        <NavBadge count={count} />
                      </span>
                    )}
                  </div>
                  <span className="tracking-wide">{t(`nav.${n.key}`)}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}
